import './post-login-modal.less';

export interface DialogConfig {
  title: string;
  contentHtml: string;
  confirmText: string;
  cancelText?: string | null;
  icon?: string;
}

export interface ModalResult {
  anchor: string;
  dialogConfig: DialogConfig;
  // Stable per-user identity from the response payload itself, used as the
  // dedup namespace. Comes back with the request's token, so it sidesteps the
  // stale-localStorage-token race that token/nickName-based keys suffered from.
  userId: string;
}

export interface ModalRegistration {
  id: string;
  priority?: number;
  fetch: () => Promise<unknown>;
  process: (payload: unknown) => ModalResult | null;
  onConfirm: () => void;
}

const registry: ModalRegistration[] = [];

/**
 * Post-login global modal framework.
 *
 * Usage - register a modal from any feature file:
 *
 * ```js
 * import { registerModal } from './post-login-modal';
 *
 * registerModal({
 *   id: 'my-feature',           // unique ID, used in dedup storage key
 *   priority: 0,                // lower = higher priority (default 0)
 *   fetch: () => Promise,       // resolves to raw response payload ({ code, data })
 *   process: (payload) => null | { anchor, dialogConfig },
 *   onConfirm: () => void,
 * });
 * ```
 *
 * Then call `runPostLoginModals()` once after login check.
 * Only the highest-priority eligible modal is shown per page load.
 *
 * `dialogConfig` shape: `{ title, contentHtml, confirmText, cancelText?, icon? }`
 *
 * `anchor`: opaque string - modal is suppressed if localStorage already holds
 * this value under the key `okj_modal_{id}_{userId}`.
 */
export const registerModal = (config: ModalRegistration): void => {
  registry.push(config);
};

const renderModal = (
  { title, contentHtml, confirmText, cancelText }: DialogConfig,
  onConfirm: () => void
): void => {
  const dialog = document.createElement('dialog');
  dialog.className = 'pl-modal';
  // contentHtml is intentionally rendered as HTML.
  // Title and button labels are injected as text nodes to prevent XSS.
  dialog.innerHTML = `
    <button class="pl-modal__close" data-action="cancel" aria-label="close">&#x2715;</button>
    <h3 class="pl-modal__title"></h3>
    <div class="pl-modal__content">${contentHtml}</div>
    <div class="pl-modal__footer">
      ${
        cancelText
          ? `<button class="pl-modal__btn pl-modal__btn--cancel" data-action="cancel"></button>`
          : ''
      }
      <button class="pl-modal__btn pl-modal__btn--confirm" data-action="confirm"></button>
    </div>
  `;
  (dialog.querySelector('.pl-modal__title') as Element).appendChild(
    document.createTextNode(title)
  );
  (dialog.querySelector('.pl-modal__btn--confirm') as Element).appendChild(
    document.createTextNode(confirmText)
  );
  if (cancelText) {
    (dialog.querySelector('.pl-modal__btn--cancel') as Element).appendChild(
      document.createTextNode(cancelText)
    );
  }

  document.body.appendChild(dialog);
  dialog.showModal();

  dialog.addEventListener('click', ({ target }) => {
    const action =
      (target as Element)
        .closest('[data-action]')
        ?.getAttribute('data-action') ?? (target === dialog ? 'cancel' : null);
    if (action === 'confirm') {
      dialog.close();
      onConfirm();
    } else if (action === 'cancel') dialog.close();
  });

  dialog.addEventListener('close', () => dialog.remove());
};

export const runPostLoginModals = async (): Promise<void> => {
  // Currently there is just carf dialog.
  // Priority is for in case we need to add more global post login modals.
  const sorted = [...registry].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
  );

  // Walk modals in priority order; show the first eligible one and stop.
  for (const modal of sorted) {
    let result: ModalResult | null;
    try {
      result = modal.process(await modal.fetch());
    } catch {
      // fetch/process error, skip this modal and try next
      continue;
    }
    // Modal decided it has nothing to show (e.g. no pending action), try next
    if (!result?.anchor) continue;

    const { anchor, dialogConfig, userId } = result;
    // Identity comes from the response payload, so it always matches the
    // user the request was made for - no localStorage token to go stale.
    const storageKey = `okj_modal_${modal.id}_${userId}`;

    let seen = false;
    try {
      seen = localStorage.getItem(storageKey) === String(anchor);
    } catch {
      // localStorage not available (e.g. privacy mode) - show the modal
    }
    if (seen) continue;

    renderModal(dialogConfig, modal.onConfirm);

    try {
      localStorage.setItem(storageKey, String(anchor));
    } catch {
      // same as above
    }
    // Modal shown, stop here - only one modal per page load
    return;
  }
};
